import React, { useState, useEffect } from 'react';
import { firestore } from '../firebase/config';
import { collection, addDoc, getDocs, deleteDoc, doc, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { useToast } from '../components/ToastContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const dummyInvoices = [
  {
    id: 1,
    type: 'Fee',
    name: 'Ali Raza',
    role: 'Student',
    amount: 15000,
    paid: 10000,
    pending: 5000,
    date: '2024-05-20 10:30',
    status: 'Partial',
    invoiceNo: 'INV-001',
  },
  {
    id: 2,
    type: 'Salary',
    name: 'Sara Khan',
    role: 'Employee',
    amount: 25000,
    paid: 25000,
    pending: 0,
    date: '2024-05-18 09:00',
    status: 'Paid',
    invoiceNo: 'INV-002',
  },
];

const Invoice = () => {
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newInvoice, setNewInvoice] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const { showSuccess, showError } = useToast();
  const [deleteInvoiceId, setDeleteInvoiceId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Search and pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('all'); // all, fee, salary
  const [currentPage, setCurrentPage] = useState(1);
  const [invoicesPerPage] = useState(10);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Load invoices from database
  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      // Simplified query - just get all invoices and filter client-side
      const invoicesRef = collection(firestore, 'invoices');
      const q = query(invoicesRef, orderBy('date', 'desc'));
      const snapshot = await getDocs(q);
      const invoiceList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      if (isLoadMore) {
        setInvoices(prev => [...prev, ...invoiceList]);
      } else {
        setInvoices(invoiceList);
      }
      
      // Update pagination state
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === invoicesPerPage);
      
    } catch (error) {
      showError('Error loading invoices: ' + error.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Filter invoices based on search
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = searchTerm === '' || 
      invoice.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.role.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = searchType === 'all' || invoice.type.toLowerCase() === searchType.toLowerCase();
    
    return matchesSearch && matchesType;
  });

  // Pagination
  const indexOfLastInvoice = currentPage * invoicesPerPage;
  const indexOfFirstInvoice = indexOfLastInvoice - invoicesPerPage;
  const currentInvoices = filteredInvoices.slice(indexOfFirstInvoice, indexOfLastInvoice);
  const totalPages = Math.ceil(filteredInvoices.length / invoicesPerPage);

  const handleSearch = () => {
    setCurrentPage(1);
    // Just reload invoices and let client-side filtering handle the search
    loadInvoices();
  };

  const handleLoadMore = () => {
    // For now, just reload all invoices since we simplified the query
    loadInvoices();
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate invoice number
  const generateInvoiceNumber = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `INV-${timestamp}-${random}`;
  };

  // Function to automatically generate invoice when pending fees are added
  const generateInvoiceForPendingFee = async (studentData, pendingAmount) => {
    try {
      const invoiceData = {
        type: 'Fee',
        name: studentData.fullName,
        role: 'Student',
        amount: parseFloat(studentData.totalFee),
        paid: parseFloat(studentData.submittedFee),
        pending: pendingAmount,
        date: new Date().toLocaleString(),
        status: pendingAmount > 0 ? 'Pending' : 'Paid',
        invoiceNo: generateInvoiceNumber(),
        studentId: studentData.id,
        autoGenerated: true,
      };

      const invoicesRef = collection(firestore, 'invoices');
      const docRef = await addDoc(invoicesRef, invoiceData);
      
      const newInvoiceWithId = { ...invoiceData, id: docRef.id };
      setInvoices(prev => [newInvoiceWithId, ...prev]);
      
      showSuccess('Invoice generated automatically for pending fee!');
      return newInvoiceWithId;
    } catch (error) {
      showError('Error generating invoice: ' + error.message);
      return null;
    }
  };

  // Print CSS for mobile-friendly printing
  const printStyles = `
    @media print {
      body * { visibility: hidden !important; }
      #invoice-print-area, #invoice-print-area * { visibility: visible !important; }
      #invoice-print-area { position: absolute; left: 0; top: 0; width: 100vw; background: #fff; color: #000; z-index: 9999; }
    }
  `;

  const handlePrint = () => {
    window.print();
  };

  const handleGenerateInvoice = () => {
    // Generate a new invoice number
    const newInvoiceNo = generateInvoiceNumber();
    
    // Create a new dummy invoice
    const generatedInvoice = {
      type: 'Fee',
      name: 'New Student',
      role: 'Student',
      amount: 20000,
      paid: 0,
      pending: 20000,
      date: new Date().toLocaleString(),
      status: 'Pending',
      invoiceNo: newInvoiceNo,
    };
    
    // Set the new invoice and form data, then open modal
    setNewInvoice(generatedInvoice);
    setFormData(generatedInvoice);
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleConfirmInvoice = async () => {
    try {
      // Add to database
      const invoicesRef = collection(firestore, 'invoices');
      const docRef = await addDoc(invoicesRef, formData);
      
      // Add to local state with the document ID
      const newInvoiceWithId = { ...formData, id: docRef.id };
      setInvoices(prev => [newInvoiceWithId, ...prev]);
      setSelected(newInvoiceWithId);
      setShowModal(false);
      setNewInvoice(null);
      setFormData({});
      showSuccess('Invoice created successfully!');
    } catch (error) {
      showError('Error creating invoice: ' + error.message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewInvoice(null);
    setFormData({});
  };

  const handleDeleteInvoice = (invoiceId) => {
    setDeleteInvoiceId(invoiceId);
    setShowDeleteModal(true);
  };
  
  const confirmDeleteInvoice = async () => {
    if (!deleteInvoiceId) return;
    try {
      await deleteDoc(doc(firestore, 'invoices', deleteInvoiceId));
      setInvoices(prev => prev.filter(inv => inv.id !== deleteInvoiceId));
      if (selected && selected.id === deleteInvoiceId) {
        setSelected(null);
      }
      showSuccess('Invoice deleted successfully!');
    } catch (error) {
      showError('Error deleting invoice: ' + error.message);
    }
    setShowDeleteModal(false);
    setDeleteInvoiceId(null);
  };
  
  const cancelDeleteInvoice = () => {
    setShowDeleteModal(false);
    setDeleteInvoiceId(null);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const invoicePrintArea = document.getElementById('invoice-print-area');
      if (!invoicePrintArea) return;
      // Temporarily make print area visible for capture
      invoicePrintArea.style.display = 'block';
      invoicePrintArea.style.pointerEvents = 'auto';
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for DOM update
      const canvas = await html2canvas(invoicePrintArea);
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 40;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 20, 20, imgWidth, imgHeight);
      pdf.save(`Invoice-${selected.invoiceNo || 'DSH'}.pdf`);
      // Hide print area again
      invoicePrintArea.style.display = '';
      invoicePrintArea.style.pointerEvents = 'none';
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-2 sm:p-6 max-w-6xl mx-auto w-full">
      <style>{printStyles}</style>
      {/* Hidden print area for invoice */}
      {selected && (
        <div id="invoice-print-area" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: -1 }} aria-hidden="true">
          <div className="invoice-container" style={{
            margin: '0 auto',
            maxWidth: '600px',
            background: '#fff',
            color: '#000',
            boxShadow: '0 0 8px rgba(0,0,0,0.08)',
            padding: '32px 24px',
            borderRadius: '16px',
            minHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <div className="header text-center mb-4">
              <img src="/DSH.png" alt="DSH Logo" className="logo mx-auto mb-2" style={{ width: 60, height: 60, borderRadius: '50%' }} />
              <div className="company-name font-bold text-lg mb-1">DIGITAL SKILLS HOUSE</div>
              <div className="invoice-title font-bold underline mb-2">{selected.type} Invoice</div>
            </div>
            <div className="invoice-info mb-4 w-full" style={{maxWidth: 480}}>
              <div className="flex justify-between mb-1"><span className="font-bold text-gray-600">Invoice No:</span><span className="font-bold">{selected.invoiceNo}</span></div>
              <div className="flex justify-between mb-1"><span className="font-bold text-gray-600">Date:</span><span className="font-bold">{selected.date}</span></div>
              <div className="flex justify-between mb-1"><span className="font-bold text-gray-600">Type:</span><span className="font-bold">{selected.type}</span></div>
            </div>
            <div className="person-info mb-4 p-3 bg-gray-100 rounded w-full" style={{maxWidth: 480}}>
              <div className="font-bold text-base mb-1">{selected.name}</div>
              <div className="text-gray-600 text-sm">{selected.role}</div>
            </div>
            {/* Financial Table */}
            <table className="mb-4 w-full" style={{maxWidth: 480, borderCollapse: 'collapse'}}>
              <tbody>
                <tr style={{borderBottom: '1px solid #e5e7eb'}}>
                  <td className="font-bold p-2 text-left" style={{background: '#eff6ff'}}>Total {selected.type}:</td>
                  <td className="font-bold p-2 text-right" style={{background: '#eff6ff'}}>{selected.amount} PKR</td>
                </tr>
                <tr style={{borderBottom: '1px solid #e5e7eb'}}>
                  <td className="font-bold p-2 text-left" style={{background: '#ecfdf5'}}>Paid:</td>
                  <td className="font-bold p-2 text-right" style={{background: '#ecfdf5'}}>{selected.paid} PKR</td>
                </tr>
                <tr>
                  <td className="font-bold p-2 text-left" style={{background: '#fef2f2'}}>Pending:</td>
                  <td className="font-bold p-2 text-right" style={{background: '#fef2f2'}}>{selected.pending} PKR</td>
                </tr>
              </tbody>
            </table>
            <div className="status text-left mb-4 w-full" style={{maxWidth: 480}}><span className="font-bold">Status:</span> <span className={`font-bold ${selected.pending === 0 ? 'text-green-600' : 'text-red-600'}`}>{selected.pending === 0 ? 'Paid' : 'Pending'}</span></div>
            <div className="footer text-center mt-6 pt-4 border-t w-full" style={{maxWidth: 480}}><div className="footer-text text-xs text-gray-600">Thank you for choosing DSH Institute & Technologies</div><div className="footer-text text-xs text-gray-600">Powered by DSH Institute & Technologies</div></div>
          </div>
        </div>
      )}
      {/* Mobile-friendly styles */}
      <style>{`
        @media (max-width: 600px) {
          .invoice-modal, .invoice-container, .invoice-print-area {
            max-width: 100vw !important;
            width: 100vw !important;
            padding: 8px !important;
            font-size: 15px !important;
          }
          .invoice-table th, .invoice-table td {
            padding: 6px !important;
            font-size: 14px !important;
          }
          .invoice-header, .invoice-footer {
            text-align: center !important;
          }
          .invoice-btn {
            width: 100%;
            margin-bottom: 8px;
            font-size: 16px;
          }
        }
      `}</style>
      
      {/* Search and Filter Section */}
      <div className="mb-6 bg-white rounded-2xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name, invoice number, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="Fee">Fee</option>
              <option value="Salary">Salary</option>
            </select>
            <button
              onClick={handleSearch}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Generate Invoice Button */}
      <div className="mb-6">
        <button 
          onClick={handleGenerateInvoice}
          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl shadow-lg hover:from-blue-700 hover:to-purple-700 font-semibold transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Generate New Invoice
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-semibold mb-4 text-blue-700">All Transactions</h3>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading invoices...</p>
              </div>
            ) : currentInvoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No invoices found. Generate your first invoice!
              </div>
            ) : (
              <>
                <ul className="divide-y divide-gray-200">
                  {currentInvoices.map(inv => (
                    <li key={inv.id} className="py-4 hover:bg-blue-50 rounded-lg px-2 transition">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 cursor-pointer" onClick={() => setSelected(inv)}>
                          <div className="font-bold text-gray-800">{inv.name} <span className="text-xs text-gray-400">({inv.role})</span></div>
                          <div className="text-sm text-gray-500">{inv.type} | {inv.date}</div>
                          <div className="text-xs text-blue-600 font-semibold">#{inv.invoiceNo}</div>
                          {inv.autoGenerated && (
                            <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mt-1">
                              Auto Generated
                            </span>
                          )}
                          {inv.isPaidInvoice && (
                            <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mt-1">
                              Paid Invoice
                            </span>
                          )}
                        </div>
                        <div className="text-right mr-3">
                          <div className={`font-bold ${inv.type === 'Fee' ? 'text-green-600' : 'text-purple-600'}`}>{inv.amount} PKR</div>
                          <div className="text-xs text-gray-400">{inv.status}</div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteInvoice(inv.id);
                          }}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                          title="Delete Invoice"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                
                {/* Pagination */}
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing {indexOfFirstInvoice + 1} to {Math.min(indexOfLastInvoice, filteredInvoices.length)} of {filteredInvoices.length} invoices
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded">
                      {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
                
                {/* Load More Button */}
                {hasMore && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingMore ? 'Loading...' : 'Load More'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        <div>
          {selected ? (
            <div className="bg-white rounded-2xl shadow-xl p-8 relative print:p-0 print:shadow-none print:bg-white">
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-2xl font-bold print:hidden">×</button>
              <div className="flex items-center gap-4 mb-6">
                <img src="/DSH.png" alt="Logo" className="w-16 h-16 rounded-full bg-white shadow" />
                <div>
                  <div className="text-2xl font-bold text-blue-900">{selected.type} Invoice</div>
                  <div className="text-sm text-gray-500">Invoice No: <span className="font-semibold">{selected.invoiceNo}</span></div>
                  <div className="text-sm text-gray-500">Date: <span className="font-semibold">{selected.date}</span></div>
                </div>
              </div>
              <div className="mb-4">
                <div className="text-lg font-semibold text-gray-700">{selected.name} <span className="text-xs text-gray-400">({selected.role})</span></div>
                <div className="text-sm text-gray-500">{selected.type === 'Fee' ? 'Student Fee Payment' : 'Employee Salary'}</div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500">Total {selected.type}</div>
                  <div className="text-xl font-bold text-blue-700">{selected.amount} PKR</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-xs text-gray-500">Paid</div>
                  <div className="text-xl font-bold text-green-700">{selected.paid} PKR</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4 col-span-2">
                  <div className="text-xs text-gray-500">Pending Dues</div>
                  <div className="text-xl font-bold text-red-700">{selected.pending} PKR</div>
                </div>
              </div>
              <div className="mb-6">
                <div className="text-sm text-gray-600">Status: <span className={`font-bold ${selected.pending === 0 ? 'text-green-600' : 'text-red-600'}`}>{selected.pending === 0 ? 'Paid' : 'Pending'}</span></div>
              </div>
              {/* Print/Download buttons below the invoice card, left and right aligned */}
              <div className="w-full flex flex-row justify-around items-center mt-4 print:hidden">
                <button onClick={handlePrint} title="Print Invoice" className="w-9 h-9 flex items-center justify-center bg-blue-600 text-white rounded-full shadow hover:bg-blue-700 transition">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 9V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><rect x="6" y="13" width="12" height="7" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 17h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                <button onClick={handleDownload} disabled={downloading} title="Download PDF" className={`w-9 h-9 flex items-center justify-center bg-green-600 text-white rounded-full shadow hover:bg-green-700 transition ${downloading ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  {downloading ? (
                    <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M19 12l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M5 19h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full min-h-[300px] text-gray-400">Select a transaction to view invoice</div>
          )}
          {/* End of invoice details card */}
          <div className="w-full flex justify-center mt-4">
            <div className="text-xs text-gray-400">Powered by DSH Institute & Technologies</div>
          </div>
        </div>
      </div>

      {/* Invoice Generation Modal */}
      {showModal && newInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-gray-300 rounded-lg shadow max-w-2xl w-full p-8 print:p-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="mb-6 text-center">
              <h1 className="text-2xl font-bold tracking-wide mb-1">DIGITAL SKILLS HOUSE</h1>
              <h2 className="text-lg font-semibold underline">Generated Invoice Details</h2>
            </div>

            {/* Invoice Information */}
            <div className="border border-gray-300 rounded mb-6 p-4">
              <h3 className="font-bold underline mb-4 text-gray-800">Invoice Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Invoice Number</label>
                  <input type="text" value={formData.invoiceNo} readOnly className="w-full border-b border-gray-400 bg-gray-100 focus:outline-none py-1 px-2 font-semibold" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <input type="text" value={formData.date} readOnly className="w-full border-b border-gray-400 bg-gray-100 focus:outline-none py-1 px-2 font-semibold" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select name="type" value={formData.type} onChange={handleFormChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2">
                    <option value="Fee">Fee</option>
                    <option value="Salary">Salary</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select name="status" value={formData.status} onChange={handleFormChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2">
                    <option value="Pending">Pending</option>
                    <option value="Partial">Partial</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Person Information */}
            <div className="border border-gray-300 rounded mb-6 p-4">
              <h3 className="font-bold underline mb-4 text-gray-800">Person Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <input type="text" name="name" value={formData.name} onChange={handleFormChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Role</label>
                  <select name="role" value={formData.role} onChange={handleFormChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2">
                    <option value="Student">Student</option>
                    <option value="Employee">Employee</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div className="border border-gray-300 rounded mb-6 p-4">
              <h3 className="font-bold underline mb-4 text-gray-800">Financial Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Total Amount (PKR)</label>
                  <input type="number" name="amount" value={formData.amount} onChange={handleFormChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Paid Amount (PKR)</label>
                  <input type="number" name="paid" value={formData.paid} onChange={handleFormChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Pending Amount (PKR)</label>
                  <input type="number" name="pending" value={formData.pending} onChange={handleFormChange} className="w-full border-b border-gray-400 bg-transparent focus:outline-none py-1 px-2" />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center gap-4">
              <button 
                onClick={handleConfirmInvoice}
                className="px-8 py-2 bg-blue-700 text-white font-semibold rounded shadow hover:bg-blue-800 transition"
              >
                Confirm & Add Invoice
              </button>
              <button 
                onClick={handleCloseModal}
                className="px-8 py-2 bg-gray-500 text-white font-semibold rounded shadow hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Invoice Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative">
            <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" onClick={cancelDeleteInvoice}>&times;</button>
            <h2 className="text-lg font-bold mb-4 text-red-600">Delete Invoice</h2>
            <p className="mb-6 text-gray-700">Are you sure you want to delete this invoice? <br/> <span className='font-semibold text-red-600'>This action cannot be undone.</span></p>
            <div className="flex justify-end gap-2">
              <button className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300" onClick={cancelDeleteInvoice}>Cancel</button>
              <button className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700" onClick={confirmDeleteInvoice}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoice; 